import React, {useState} from "react";
import {Template} from "../../../../components/template/template.jsx";
import {Card} from "../../../../components/card/card.jsx";
import Button from "../../../../components/button/button.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import SentryRow from "../../../../components/template/sentry-row.jsx";
import {useParams} from "react-router";
import {FaPlus} from "react-icons/fa";
import {useQuery} from "@tanstack/react-query";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import projectUtils from "../../../../utils/project-utils.js";
import ReservationCauseService from "../../../../services/dispatch/reservation-cause-service.js";

const API_DESPACHO_WEB = import.meta.env.VITE_API_DESPACHO_WEB;
const REQUIRED_BG = "!bg-[#fdfdee]";
const REQUIRED_LABEl_BG = "!bg-[linear-gradient(to_bottom,transparent_50%,#fdfdee_50%)]";

const initialReservationCause = {name: ""};

const ReservationCauseForm = () => {
    const navigate = navigateHook();
    const {id} = useParams();
    const [state, setState] = useState(initialReservationCause);
    const [isSaving, setIsSaving] = useState(false);
    const [isSavingAndAdding, setIsSavingAndAdding] = useState(false);

    const {isLoading: isQueryLoading} = useQuery({
        queryFn: async () => {
            const data = await ReservationCauseService.get(id).catch(() => null);
            if (!data) {
                navigate("/404");
                return;
            }

            const {reservationCause} = data;
            setState({id: reservationCause.id, name: reservationCause.name ?? ""});
            return reservationCause;
        },
        queryKey: ["dispatch-reservation-cause", id],
        enabled: !!id,
    });

    const onChange = ({target: {value, id}}) => setState(prev => ({...prev, [id]: value}));

    const persist = (addAfter = false) => {
        if (!state.name) {
            projectUtils.swalErrorMessage("", "O campo \"Nome\" é obrigatório.", "Ok");
            return;
        }

        const payload = {name: state.name};

        const setSaving = addAfter ? setIsSavingAndAdding : setIsSaving;
        setSaving(true);
        const response = id ? ReservationCauseService.update(id, payload) : ReservationCauseService.save(payload);
        response
            .then((data) => {
                if (addAfter) {
                    if (id) {
                        projectUtils.success(true);
                        navigate(API_DESPACHO_WEB + "/reservation-cause/form", false);
                    } else {
                        projectUtils.success(false);
                        setState(initialReservationCause);
                    }
                    return;
                }

                projectUtils.success(true);

                if (id) {
                    navigate(API_DESPACHO_WEB + "/reservation-cause", false);
                    return;
                }

                navigate(`${API_DESPACHO_WEB}/reservation-cause/form/${data.id}`, false);
            })
            .catch((error) => {
                console.error(error);
                const errMessage = error.response?.data?.errorMessages ?? "Não foi possível salvar.";
                projectUtils.swalErrorMessage("", errMessage, "Ok");
            })
            .finally(() => setSaving(false));
    };

    const destroy = () => {
        projectUtils.swalMessage("warning", "Excluir", `Excluir o motivo da reserva de imagem "${state.name}"?`, "Sim", true, "Não",
            ({isConfirmed}) => {
                if (!isConfirmed) return;
                ReservationCauseService.destroy(id)
                    .then(() => {
                        projectUtils.success(true, "Excluído com sucesso");
                        navigate(API_DESPACHO_WEB + "/reservation-cause", false);
                    })
                    .catch((error) => {
                        console.error(error);
                        const errMessage = error.response?.data?.errorMessages ?? "Não foi possível excluir.";
                        projectUtils.swalErrorMessage("", errMessage, "Ok");
                    });
            });
    };

    const newReservationCause = () => navigate(API_DESPACHO_WEB + "/reservation-cause/form", false);

    return (
        <Template isLoading={isSaving || isSavingAndAdding || isQueryLoading}>
            <Template.Header>
                <Template.Title>Motivo da reserva de imagem</Template.Title>
                {id &&
                    <Button target="primary-outline" onClick={newReservationCause}><FaPlus/>Adicionar motivo</Button>
                }
            </Template.Header>
            <Template.Body className="flex flex-col space-y-2">
                <Card>
                    <Card.Body>
                        <SentryRow>
                            <InputForm classNameWrapper="col-span-12" inputClass={REQUIRED_BG} labelClass={REQUIRED_LABEl_BG} id="name" label="Nome *" value={state.name} onChange={onChange}/>
                        </SentryRow>
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Body className="flex gap-2 justify-end">
                        {id && <Button target="danger" onClick={destroy} actionFeedbackType='delete'>Excluir</Button>}
                        <Button target="primary-outline" onClick={() => persist(true)} loading={isSavingAndAdding} actionFeedbackType='save'>Salvar e adicionar novo</Button>
                        <Button onClick={() => persist(false)} loading={isSaving} actionFeedbackType='save'>Salvar</Button>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default ReservationCauseForm;